// FRONTEND API SERVICE - COMPLETO con supporto cartelle
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Helper per le chiamate con token
const fetchWithAuth = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };
  
  // Non impostare Content-Type per FormData (multipart)
  if (options.body instanceof FormData) {
    delete defaultHeaders['Content-Type'];
  }
  
  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  return response;
};

// Token management
const setToken = (token) => localStorage.setItem('token', token);
const getToken = () => localStorage.getItem('token');
const removeToken = () => localStorage.removeItem('token');

const api = {
  // ==================== AUTH ====================
  
  register: async (email, username, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, username, password }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante la registrazione');
    
    if (data.token) setToken(data.token);
    return data;
  },
  
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Credenziali non valide');
    
    if (data.token) setToken(data.token);
    return data;
  },
  
  getCurrentUser: async () => {
    const response = await fetchWithAuth('/auth/me');
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel recupero dell\'utente');
    return data;
  },
  
  refreshToken: async () => {
    const response = await fetchWithAuth('/auth/refresh', { method: 'POST' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel rinnovo del token');
    if (data.token) setToken(data.token);
    return data;
  },
  
  logout: () => {
    removeToken();
    window.location.href = '/login';
  },
  
  // ==================== DOCUMENTI ====================
  
  /**
   * Carica un file - AGGIORNATO con folder_path
   */
  uploadFile: async (file, folderPath = '/') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder_path', folderPath);

    const response = await fetchWithAuth('/documents/upload', {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante il caricamento del file');
    return data;
  },

  // ==================== ANONIMIZZAZIONE ====================

  startAnonymization: async (file, folderPath = '/') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder_path', folderPath);

    const response = await fetchWithAuth('/anonymization/start', {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante l\'avvio dell\'anonimizzazione');
    return data;
  },

  getAnonymizationStatus: async (jobId) => {
    const response = await fetchWithAuth(`/anonymization/status/${encodeURIComponent(jobId)}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante il polling dello stato');
    return data;
  },

  getAnonymizedText: async (relativeUrl) => {
    const params = new URLSearchParams({ url: relativeUrl });
    const response = await fetchWithAuth(`/anonymization/text?${params.toString()}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante il download del testo anonimizzato');
    return data;
  },

  finalizeAnonymizedDocument: async ({ jobId, filename, folderPath, editedText, entities, anonymizerMetadata }) => {
    const response = await fetchWithAuth('/anonymization/finalize', {
      method: 'POST',
      body: JSON.stringify({
        job_id: jobId,
        filename,
        folder_path: folderPath,
        edited_text: editedText,
        entities: entities || [],
        anonymizer_metadata: anonymizerMetadata || {},
      }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante il salvataggio del documento');
    return data;
  },

  // --- Coda persistente (sopravvive a refresh/chiusura tab) ---

  getPendingAnonymizations: async () => {
    const response = await fetchWithAuth('/anonymization/pending');
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel recupero dei job pendenti');
    return data;
  },

  pollPendingAnonymization: async (pendingId) => {
    const response = await fetchWithAuth(`/anonymization/pending/${pendingId}/poll`, {
      method: 'POST',
    });
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(data.error || 'Errore durante il polling');
      err.status = response.status;
      throw err;
    }
    return data;
  },

  finalizePendingAnonymization: async (pendingId, editedText) => {
    const response = await fetchWithAuth(`/anonymization/pending/${pendingId}/finalize`, {
      method: 'POST',
      body: JSON.stringify({ edited_text: editedText }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante il salvataggio del documento');
    return data;
  },

  deletePendingAnonymization: async (pendingId) => {
    const response = await fetchWithAuth(`/anonymization/pending/${pendingId}`, {
      method: 'DELETE',
    });
    if (!response.ok && response.status !== 204) {
      let msg = 'Errore durante la rimozione';
      try {
        const data = await response.json();
        msg = data.error || msg;
      } catch (_) { /* ignore */ }
      throw new Error(msg);
    }
    return true;
  },
  
  /**
   * Ottieni lista documenti - AGGIORNATO con filtro folder_path
   */
  getDocuments: async (folderPath = null) => {
    const params = new URLSearchParams();
    if (folderPath !== null && folderPath !== undefined) {
      params.append('folder_path', folderPath);
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/documents/?${queryString}` : '/documents/';
    
    const response = await fetchWithAuth(endpoint);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel recupero dei documenti');
    return data;
  },
  
  /**
   * NUOVO: Ottieni struttura cartelle
   */
  getFolders: async () => {
    const response = await fetchWithAuth('/documents/folders');
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel recupero delle cartelle');
    return data;
  },
  
  /**
   * NUOVO: Crea una nuova cartella
   */
  createFolder: async (folderPath) => {
    const response = await fetchWithAuth('/documents/folders/create', {
      method: 'POST',
      body: JSON.stringify({ folder_path: folderPath }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante la creazione della cartella');
    return data;
  },
  
  /**
   * NUOVO: Rinomina una cartella
   */
  renameFolder: async (oldPath, newPath) => {
    const response = await fetchWithAuth('/documents/folders/rename', {
      method: 'PUT',
      body: JSON.stringify({ old_path: oldPath, new_path: newPath }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante la rinomina della cartella');
    return data;
  },
  
  /**
   * NUOVO: Elimina una cartella
   */
  deleteFolder: async (folderPath) => {
    const response = await fetchWithAuth(`/documents/folders/delete?folder_path=${encodeURIComponent(folderPath)}`, {
      method: 'DELETE',
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante l\'eliminazione della cartella');
    return data;
  },
  
  /**
   * NUOVO: Sposta un documento in un'altra cartella
   */
  moveDocument: async (documentId, folderPath) => {
    const response = await fetchWithAuth('/documents/move', {
      method: 'PUT',
      body: JSON.stringify({ document_id: documentId, folder_path: folderPath }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante lo spostamento del documento');
    return data;
  },
  
  getDocument: async (documentId) => {
    const response = await fetchWithAuth(`/documents/${documentId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'non è stato possibile aprire il documento richiesto, ti preghiamo di verificare se il documento è ancora disponibile o se è stato eliminato nella sezione \'Documenti\'.');
    return data;
  },

  /**
   * Scarica il file binario originale (non anonimizzato) di un documento.
   * Restituisce un oggetto { blob, mime, filename } pronto per l'anteprima
   * via URL.createObjectURL.
   */
  getDocumentOriginal: async (documentId) => {
    const response = await fetchWithAuth(`/documents/${documentId}/original`);
    if (!response.ok) {
      let msg = 'File originale non disponibile';
      try {
        const data = await response.json();
        msg = data.error || msg;
      } catch (_) { /* response non-JSON */ }
      const err = new Error(msg);
      err.status = response.status;
      throw err;
    }
    const blob = await response.blob();
    const mime = response.headers.get('Content-Type') || blob.type || 'application/octet-stream';
    // Estrai il filename dal Content-Disposition se presente
    let filename = null;
    const disposition = response.headers.get('Content-Disposition');
    if (disposition) {
      const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i);
      if (match && match[1]) {
        try { filename = decodeURIComponent(match[1]); } catch (_) { filename = match[1]; }
      }
    }
    return { blob, mime, filename };
  },
  
  deleteDocument: async (documentId) => {
    const response = await fetchWithAuth(`/documents/${documentId}`, {
      method: 'DELETE',
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante l\'eliminazione');
    return data;
  },
  
  processDocument: async (documentId, options = {}) => {
    const response = await fetchWithAuth(`/documents/${documentId}/process`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante il processing');
    return data;
  },
  
  batchProcess: async (documentIds, options = {}) => {
    const response = await fetchWithAuth('/documents/batch-process', {
      method: 'POST',
      body: JSON.stringify({ document_ids: documentIds, ...options }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante il batch processing');
    return data;
  },
  
  getDocumentChunks: async (documentId) => {
    const response = await fetchWithAuth(`/documents/${documentId}/chunks`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel recupero dei chunks');
    return data;
  },
  
  getStats: async () => {
    const response = await fetchWithAuth('/documents/stats');
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel recupero delle statistiche');
    return data;
  },

  // ==================== BACKFILL ORIGINALI (ADMIN) ====================

  /**
   * Preview backfill: invia i file e ottiene i candidati per ognuno.
   * Non salva nulla lato server; serve a costruire la mappatura UI prima di confermare.
   */
  previewBackfillOriginals: async (files) => {
    const formData = new FormData();
    const clientIds = [];
    files.forEach((f, idx) => {
      const cid = `cf_${idx}_${Date.now()}`;
      clientIds.push(cid);
      formData.append('files', f);
    });
    formData.append('client_ids', JSON.stringify(clientIds));

    const response = await fetchWithAuth('/admin/originals/preview', {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel preview del backfill');
    // Allega i client_ids al risultato per disambiguazione lato client
    return { ...data, client_ids: clientIds };
  },

  /**
   * Apply backfill: invia i file + il mapping confermato.
   * mapping: [{ client_id, document_id }]
   * files deve avere lo stesso ordine usato per generare i client_ids.
   */
  applyBackfillOriginals: async (files, clientIds, mapping) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    formData.append('client_ids', JSON.stringify(clientIds));
    formData.append('mapping', JSON.stringify(mapping));

    const response = await fetchWithAuth('/admin/originals/apply', {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nell\'applicazione del backfill');
    return data;
  },

  /**
   * Parsa un XLSX di mapping (Nome Originale / Nome Anonimizzato) e ritorna
   * le righe risolte con il Document associato per ognuna.
   */
  parseBackfillMapping: async (xlsxFile) => {
    const formData = new FormData();
    formData.append('file', xlsxFile);

    const response = await fetchWithAuth('/admin/originals/parse-mapping', {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore parsing del mapping XLSX');
    return data;
  },

  /**
   * Apply mapping XLSX: invia i file binari + la lista [{filename, document_id}]
   * costruita lato client dal mapping risolto.
   */
  applyBackfillMapping: async (files, mapping) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    formData.append('mapping', JSON.stringify(mapping));

    const response = await fetchWithAuth('/admin/originals/apply-mapping', {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nell\'applicazione del mapping');
    return data;
  },
  
  // ==================== QUERY RAG ====================
  
  /**
   * Esegui una query RAG - AGGIORNATO con folder_path
   */
  performQuery: async (query, options = {}) => {
    const response = await fetchWithAuth('/rag/query', {
      method: 'POST',
      body: JSON.stringify({ query, ...options }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante la query');
    return data;
  },
  
  getQueryHistory: async (limit = 50) => {
    const response = await fetchWithAuth(`/rag/history?limit=${limit}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel recupero dello storico');
    return data;
  },
  
  deleteQueryHistory: async (queryId) => {
    const response = await fetchWithAuth(`/rag/history/${queryId}`, {
      method: 'DELETE',
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante l\'eliminazione');
    return data;
  },
  
  getRagStats: async () => {
    const response = await fetchWithAuth('/rag/stats');
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel recupero delle statistiche');
    return data;
  },

  // ==================== GESTIONE UTENTI (SUPERUSER) ====================
  
  /**
   * Lista tutti gli utenti (con paginazione e filtri)
   */
  listUsers: async (page = 1, perPage = 20, search = '', isActive = null) => {
    let url = `/users/?page=${page}&per_page=${perPage}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (isActive !== null) {
      url += `&is_active=${isActive}`;
    }
    
    const response = await fetchWithAuth(url);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nel recupero degli utenti');
    }
    
    return data;
  },
  
  /**
   * Ottieni dettagli di un utente specifico
   */
  getUser: async (userId) => {
    const response = await fetchWithAuth(`/users/${userId}`);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nel recupero dell\'utente');
    }
    
    return data;
  },
  
  /**
   * Crea un nuovo utente
   */
  createUser: async (email, username, password, isActive = true, isSuperuser = false, companyId = null) => {
    const response = await fetchWithAuth('/users/', {
      method: 'POST',
      body: JSON.stringify({
        email,
        username,
        password,
        is_active: isActive,
        is_superuser: isSuperuser,
        company_id: companyId
      }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nella creazione dell\'utente');
    }
    
    return data;
  },
  
  /**
   * Modifica un utente esistente
   */
  updateUser: async (userId, updates) => {
    const response = await fetchWithAuth(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nell\'aggiornamento dell\'utente');
    }
    
    return data;
  },
  
  /**
   * Elimina un utente
   */
  deleteUser: async (userId) => {
    const response = await fetchWithAuth(`/users/${userId}`, {
      method: 'DELETE',
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nell\'eliminazione dell\'utente');
    }
    
    return data;
  },
  
  /**
   * Ottieni statistiche utenti
   */
  getUserStats: async () => {
    const response = await fetchWithAuth('/users/stats');
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nel recupero delle statistiche');
    }
    
    return data;
  },

  // ==================== GESTIONE AZIENDE (SUPERUSER) ====================
  
  /**
   * Lista tutte le aziende (con paginazione e filtri)
   */
  listCompanies: async (page = 1, perPage = 20, search = '', isActive = null) => {
    let url = `/companies/?page=${page}&per_page=${perPage}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (isActive !== null) {
      url += `&is_active=${isActive}`;
    }
    
    const response = await fetchWithAuth(url);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nel recupero delle aziende');
    }
    
    return data;
  },
  
  /**
   * Ottieni dettagli di un'azienda specifica
   */
  getCompany: async (companyId) => {
    const response = await fetchWithAuth(`/companies/${companyId}`);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nel recupero dell\'azienda');
    }
    
    return data;
  },
  
  /**
   * Crea una nuova azienda
   */
  createCompany: async (ragioneSociale, logo = null, isActive = true) => {
    const response = await fetchWithAuth('/companies/', {
      method: 'POST',
      body: JSON.stringify({
        ragione_sociale: ragioneSociale,
        logo: logo,
        is_active: isActive
      }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nella creazione dell\'azienda');
    }
    
    return data;
  },
  
  /**
   * Modifica un'azienda esistente
   */
  updateCompany: async (companyId, updates) => {
    const response = await fetchWithAuth(`/companies/${companyId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nell\'aggiornamento dell\'azienda');
    }
    
    return data;
  },
  
  /**
   * Elimina un'azienda (CASCADE: elimina anche gli utenti associati)
   */
  deleteCompany: async (companyId) => {
    const response = await fetchWithAuth(`/companies/${companyId}`, {
      method: 'DELETE',
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Errore nell\'eliminazione dell\'azienda');
    }
    
    return data;
  },
  
  /**
   * Ottieni statistiche aziende
   */
  getCompanyStats: async () => {
    const response = await fetchWithAuth('/companies/stats');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Errore nel recupero delle statistiche');
    }

    return data;
  },

  /**
   * Ottieni utilizzo di una specifica azienda (admin)
   */
  getCompanyUsage: async (companyId) => {
    const response = await fetchWithAuth(`/companies/${companyId}/usage`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel recupero dell\'utilizzo');
    return data;
  },

  /**
   * Ottieni utilizzo dell'azienda dell'utente corrente (utenti non-admin)
   */
  getMyCompanyUsage: async () => {
    const response = await fetchWithAuth('/companies/my-usage');
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore nel recupero dell\'utilizzo');
    return data;
  },

  /**
   * Reset manuale dei contatori mensili di un'azienda (solo superuser)
   */
  resetCompanyUsage: async (companyId) => {
    const response = await fetchWithAuth(`/companies/${companyId}/reset-usage`, { method: 'POST' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Errore durante il reset dei contatori');
    return data;
  },
};

export { api, setToken, getToken, removeToken };
export default api;